import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronCircleLeft, FaChevronCircleRight, FaChevronDown, FaWhatsapp } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  AGE_RANGE,
  EDUCATION_LEVELS,
  EMPLOYMENT_STATUS,
  FIELDS,
  GENDER,
  HEARD_ABOUT_US,
  NATIONALITIES,
  SECTORS,
  TYPES,
  UNIVERSITIES,
} from "../constants/common";

const Registration = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [cities, setCities] = useState([]);
  const [referralCode, setReferralCode] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [hasReferralCode, setHasReferralCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [futureStationsList, setFutureStationsList] = useState([]);

  // dynamic validation schema based on step
  const validationSchema = (step) =>
    Yup.object({
      firstName: Yup.string().required(t("required")),
      secondName: Yup.string().required(t("required")),
      thirdName: Yup.string().required(t("required")),
      lastName: Yup.string().required(t("required")),
      phone: Yup.string().required(t("required")),
      email:
        step === 2
          ? Yup.string().email(t("invalidEmail")).required(t("required"))
          : Yup.string().email(t("invalidEmail")),
      confirmEmail:
        step === 2
          ? Yup.string()
              .oneOf([Yup.ref("email"), null], t("emailsMustMatch"))
              .required(t("required"))
          : Yup.string(),
      ageRange: Yup.number().required(t("required")),
      gender: Yup.number().required(t("required")),
      city: Yup.string().required(t("required")),
      university: Yup.string().when("studentType", {
        is: (val) => val == TYPES.UNIVERSITY,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      universityOther: Yup.string().when(["studentType", "university"], {
        is: (type, university) => type == TYPES.UNIVERSITY && university == 13,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      schoolName: Yup.string().when("studentType", {
        is: (val) => val == TYPES.SCHOOL,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      fieldOfStudy: Yup.string().when("studentType", {
        is: (val) => val == TYPES.UNIVERSITY,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      educationLevel: Yup.string().when("studentType", {
        is: (val) => val == TYPES.OTHER,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      fieldOfStudy: Yup.string().when("studentType", {
        is: (val) => val == TYPES.OTHER || val == TYPES.UNIVERSITY,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      employmentStatus: Yup.string().when("studentType", {
        is: (val) => val == TYPES.OTHER,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      sector: Yup.string().when(["studentType", "employmentStatus"], {
        is: (type, status) => type == TYPES.OTHER && status == 1,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      studentType: Yup.number().required(t("required")),
      nationality: Yup.number().required(t("required")),
      ministerialID: Yup.string().when("studentType", {
        is: (val) => val == TYPES.SCHOOLTEACHER,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      // Conditional validation
      nationalNumber: Yup.string().when("nationality", {
        is: (val) => val == NATIONALITIES.JORDAN,
        then: (schema) => schema.required(t("required")).matches(/^\d{10}$/, t("idMustBe10Digits")),
        otherwise: (schema) => schema.notRequired(),
      }),
      documentId: Yup.string().when("nationality", {
        is: (val) => val == NATIONALITIES.SYRIA,
        then: (schema) => schema.required(t("required")).matches(/^\d{10}$/, t("idMustBe10Digits")),
        otherwise: (schema) => schema.notRequired(),
      }),
      documentFile: Yup.mixed().when("nationality", {
        is: (val) => val == NATIONALITIES.SYRIA,
        then: (schema) =>
          schema
            .required(t("required"))
            .test("fileType", t("documentFileInvalidType"), (value) =>
              value ? ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(value.type) : false
            )
            .test("fileSize", t("documentFileTooLarge"), (value) =>
              value ? value.size <= 5 * 1024 * 1024 : false
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      heardAboutUs: Yup.number().required(t("required")),
      futureStation: Yup.string().when("heardAboutUs", {
        is: (val) => val == HEARD_ABOUT_US.FUTURE_STATIONS,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      password:
        step === 2
          ? Yup.string()
              .min(8, t("minPassword", { count: 8 }))
              .max(20, t("field_max_20"))
              .required(t("required"))
          : Yup.string(),
      confirmPassword:
        step === 2
          ? Yup.string()
              .oneOf([Yup.ref("password"), null], t("passwordsMustMatch"))
              .required(t("required"))
          : Yup.string(),
    });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      secondName: "",
      thirdName: "",
      lastName: "",
      phone: "",
      email: "",
      confirmEmail: "",
      ageRange: "",
      gender: "",
      heardAboutUs: "",
      city: "",
      studentType: "",
      schoolName: "",
      ministerialID: "",
      nationality: "",
      nationalNumber: "",
      documentId: "",
      documentFile: null,
      password: "",
      confirmPassword: "",
      referralCode: "",
      futureStation: "",
      university: "",
      universityOther: "",
      fieldOfStudy: "",
      fieldOfStudyOther: "",
      employmentStatusOther: "",
      educationLevelOther: "",
      educationLevel: "",
      employmentStatus: "",
      sector: "",
    },
    validationSchema: validationSchema(step),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      setApiError("");

      try {
        const payload = {
          first_name: values.firstName,
          second_name: values.secondName,
          third_name: values.thirdName,
          last_name: values.lastName,
          phone: `962${values.phone.replace(/^0/, "")}`,
          email: values.email,
          age_range_id: values.ageRange,
          gender_id: values.gender,
          heard_about_us_id: values.heardAboutUs,
          governorate_id: values.city,

          // University
          university_id: values.university,
          ...(values.university == 13 && { university_name: values.universityOther }),

          // School
          ...(values.studentType == TYPES.SCHOOL && { school_name: values.schoolName }),

          // Field of Study flow
          ...(values.studentType == TYPES.UNIVERSITY
            ? {
                student_major_id: values.fieldOfStudy,
                ...(values.fieldOfStudy == 12 ? { student_major_name: values.fieldOfStudyOther } : {}),
              }
            : {}),
          ...(values.studentType == TYPES.OTHER
            ? {
                other_major_id: values.fieldOfStudy,
                ...(values.fieldOfStudy == 12 ? { other_major_name: values.fieldOfStudyOther } : {}),
              }
            : {}),

          ...(values.heardAboutUs == HEARD_ABOUT_US.FUTURE_STATIONS
            ? {
                future_station_id: values.futureStation,
              }
            : {}),

          // Education Level
          education_level_id: values.educationLevel,
          // Sector
          sector_id: values.sector, // assuming sector_id is expected

          // Employment Status
          employment_status_id: values.employmentStatus,
          ...(values.employmentStatus == 3 && { employment_status: values.employmentStatusOther }),

          // Student type & IDs
          type: values.studentType,
          nationality_id: values.nationality,
          national_id: values.nationalNumber ? values.nationalNumber : values.documentId,
          ministerial_id: values.studentType == TYPES.SCHOOLTEACHER ? values.ministerialID : null,

          // Passwords & codes
          password: values.password,
          password_confirmation: values.confirmPassword,
          referral_code: values.referralCode || null,
          promo_code_id: values.promoCode || null,
          digirise: sessionStorage.getItem("digirise"),
        };

        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });
        if (values.nationality == NATIONALITIES.SYRIA && values.documentFile) {
          formData.append("syrian_id", values.documentFile);
        }

        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/register`, formData, {
          headers: {
            lang: i18n.language,
            "Content-Type": "multipart/form-data",
          },
        });

        const tempUser = {
          firstName: formik.values.firstName,
          email: formik.values.email,
        };
        localStorage.setItem("tempUser", JSON.stringify(tempUser));

        window.fbq("track", "CompleteRegistration");

        navigate("/auth/on-boarding");
        setStep(1);
        formik.resetForm();
      } catch (error) {
        if (error.response) {
          const data = error.response.data;

          let detailedErrors = [];
          if (data.errors) {
            detailedErrors = Object.values(data.errors).flat();
          }

          const errorMessage =
            detailedErrors.length > 0 ? detailedErrors.join(" / ") : data.message || "Registration failed";

          setApiError(errorMessage);
        } else {
          setApiError("An unexpected error occurred");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/governorates`, {
        headers: {
          lang: i18n.language,
        },
      });
      return response?.data?.data;
    } catch (error) {
      console.error("Error fetching cities:", error);
      setApiError("Failed to load cities. Please try again later.");
      return [];
    }
  };

  const fetchFutureStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/future-station-promo-codes`, {
        headers: {
          lang: i18n.language,
        },
      });

      return response?.data;
    } catch (error) {
      console.error("Error fetching cities:", error);
      setApiError("Failed to load cities. Please try again later.");
      return [];
    }
  };

  useEffect(() => {
    const loadCities = async () => {
      const fetchedCities = await fetchCities();
      setCities(fetchedCities);
    };
    loadCities();
    const loadFutureStations = async () => {
      const futureStationsList = await fetchFutureStations();

      setFutureStationsList(futureStationsList);
    };
    loadFutureStations();
  }, [i18n.language]);

  useEffect(() => {
    if (formik.values.nationality == NATIONALITIES.SYRIA) {
      // Clear Jordanian national ID
      formik.setFieldValue("nationalNumber", "");
    } else if (formik.values.nationality == NATIONALITIES.JORDAN) {
      // Clear Syrian document ID + attachment
      formik.setFieldValue("documentId", "");
      formik.setFieldValue("documentFile", null);
    }
  }, [formik.values.nationality]);

  // Extract query param (if exists)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      setHasReferralCode(true);
      setReferralCode(ref);
      formik.setFieldValue("referralCode", ref);
    }
  }, [location.search]);

  // Extract query param (if exists)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("promo");
    if (ref) {
      setPromoCode(ref);
      formik.setFieldValue("promoCode", ref);
    }
  }, [location.search]);

  useEffect(() => {
    if (formik.values.futureStation) {
      const selected = futureStationsList?.find((s) => s.id == formik.values.futureStation);
      if (selected?.code) {
        formik.setFieldValue("promoCode", selected.code);
      }
    } else if (formik.values.heardAboutUs == HEARD_ABOUT_US.FUTURE_STATIONS) {
      formik.setFieldValue("promoCode", "");
    }
  }, [formik.values.futureStation]);

  useEffect(() => {
    const type = formik.values.studentType;

    // Reset EVERYTHING related to type switching
    formik.setFieldValue("ministerialID", "");
    formik.setFieldValue("schoolName", "");
    formik.setFieldValue("university", "");
    formik.setFieldValue("universityOther", "");
    formik.setFieldValue("futureStation", "");
    formik.setFieldValue("fieldOfStudy", "");
    formik.setFieldValue("fieldOfStudyOther", "");
    formik.setFieldValue("educationLevel", "");
    formik.setFieldValue("educationLevelOther", "");
    formik.setFieldValue("employmentStatus", "");
    formik.setFieldValue("employmentStatusOther", "");
    formik.setFieldValue("sector", "");
  }, [formik.values.studentType]);

  // helper to show error
  const ErrorMsg = ({ name }) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className="text-sm text-red-500">{formik.errors[name]}</div>
    ) : null;

  return (
    <>
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/+962790488840" // replace with your number in international format
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl md:bottom-5"
      >
        <FaWhatsapp size={28} />
      </a>
      <div
        className="flex min-h-screen flex-col bg-gray-100 text-sm text-[#616262]"
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
      >
        {/* Top bar */}
        <div className="border-b border-gray-200 bg-white py-4">
          <div className="z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-6">
            <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-start">
              {/* Logo */}
              <img
                onClick={() => navigate("/auth/landing")}
                src="/logo-blue.svg"
                alt="Logo"
                className="h-10 w-auto cursor-pointer sm:h-8 ltr:pr-3 rtl:pl-3"
              />
            </div>
            {/* <LanguageSwitcher defaultTheme /> */}
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-3xl rounded-xl border border-gray-200 bg-white p-8 shadow-lg">
            {/* API Error Message */}
            {apiError && (
              <div className="mb-4 rounded-lg border border-red-500 bg-red-100 px-4 py-2 text-red-700">
                {apiError}
              </div>
            )}

            <>
              {step === 1 && (
                <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 md:grid-cols-2">
                  {/* First Name */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("firstNameLabel")}</label>
                      <ErrorMsg name="firstName" />
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      placeholder={t("firstNamePlaceholder")}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.firstName && formik.errors.firstName
                          ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                          : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    />
                  </div>

                  {/* Second Name */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("secondNameLabel")}</label>
                      <ErrorMsg name="secondName" />
                    </div>
                    <input
                      type="text"
                      name="secondName"
                      placeholder={t("secondNamePlaceholder")}
                      value={formik.values.secondName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.secondName && formik.errors.secondName
                          ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                          : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    />
                  </div>

                  {/* Third Name */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("thirdNameLabel")}</label>
                      <ErrorMsg name="thirdName" />
                    </div>
                    <input
                      type="text"
                      name="thirdName"
                      placeholder={t("thirdNamePlaceholder")}
                      value={formik.values.thirdName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.thirdName && formik.errors.thirdName
                          ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                          : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("lastNameLabel")}</label>
                      <ErrorMsg name="lastName" />
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      placeholder={t("lastNamePlaceholder")}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.lastName && formik.errors.lastName
                          ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                          : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    />
                  </div>
                  {/* Jordan Phone Number */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("phoneLabel")}</label>
                      <ErrorMsg name="phone" />
                    </div>
                    <div className="flex" dir="ltr">
                      <span className="flex items-center rounded-l-xl border border-r-0 bg-white px-3 text-sm text-[#616262]">
                        +962
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        placeholder={t("phonePlaceholder")}
                        value={formik.values.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ""); // only digits
                          if (value.length <= 10) formik.setFieldValue("phone", value); // limit to 9 digits
                        }}
                        onBlur={formik.handleBlur}
                        maxLength={10}
                        className={`w-full rounded-r-2xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.phone && formik.errors.phone
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Age Range */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("ageRangeLabel")}</label>
                      <ErrorMsg name="ageRange" />
                    </div>
                    <select
                      name="ageRange"
                      value={formik.values.ageRange}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.ageRange && formik.errors.ageRange
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("ageRangePlaceholder")}
                      </option>
                      <option value={AGE_RANGE.TEEN}>13 – 17</option>
                      <option value={AGE_RANGE.YOUNG}>18 – 34</option>
                      <option value={AGE_RANGE.ADULT}>34+</option>
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>

                  {/* Gender */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("genderLabel")}</label>
                      <ErrorMsg name="gender" />
                    </div>
                    <select
                      name="gender"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.gender && formik.errors.gender
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("genderPlaceholder")}
                      </option>
                      <option value={GENDER.MALE}>{t("male")}</option>
                      <option value={GENDER.FEMALE}>{t("female")}</option>
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>

                  {/* City */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">{t("cityLabel")}</label>
                      <ErrorMsg name="city" />
                    </div>
                    <select
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.city && formik.errors.city
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("cityPlaceholder")}
                      </option>
                      {cities?.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>

                  {/* Student Type */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">
                        {t("studentTypeLabel")}
                      </label>
                      <ErrorMsg name="studentType" />
                    </div>
                    <select
                      name="studentType"
                      value={formik.values.studentType}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.studentType && formik.errors.studentType
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("studentTypePlaceholder")}
                      </option>
                      <option value={TYPES.SCHOOL}> {t("schoolStudent")}</option>
                      <option value={TYPES.UNIVERSITY}> {t("universityStudent")}</option>
                      <option value={TYPES.SCHOOLTEACHER}> {t("schoolTeacher")}</option>
                      <option value={TYPES.OTHER}> {t("other")}</option>
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>
                  {/* School Name */}
                  {formik.values.studentType == TYPES.SCHOOL && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("schoolNameLabel")}
                        </label>
                        <ErrorMsg name="schoolName" />
                      </div>
                      <input
                        type="text"
                        name="schoolName"
                        placeholder={t("schoolNamePlaceholder")}
                        value={formik.values.schoolName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.schoolName && formik.errors.schoolName
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>
                  )}
                  {/* University */}
                  {formik.values.studentType == TYPES.UNIVERSITY && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("universityLabel")}
                        </label>
                        <ErrorMsg name="university" />
                      </div>

                      <select
                        name="university"
                        value={formik.values.university}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm ${
                          formik.touched.university && formik.errors.university
                            ? "border-red-500"
                            : "focus:border-purple-500"
                        }`}
                      >
                        <option value="" disabled>
                          {t("universityPlaceholder")}
                        </option>

                        {UNIVERSITIES.map((uni) => (
                          <option key={uni.id} value={uni.id}>
                            {t(`universities.${uni.id}`)}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}
                  {formik.values.studentType == TYPES.UNIVERSITY && formik.values.university == 13 && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("universityOtherPlaceholder")}
                        </label>
                        <ErrorMsg name="universityOther" />
                      </div>

                      <input
                        type="text"
                        name="universityOther"
                        placeholder={t("specifyUniversity")}
                        value={formik.values.universityOther}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.universityOther && formik.errors.universityOther
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>
                  )}
                  {/* Field of Study */}
                  {(formik.values.studentType == TYPES.OTHER ||
                    formik.values.studentType == TYPES.UNIVERSITY) && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("fieldOfStudyLabel")}
                        </label>
                        <ErrorMsg name="fieldOfStudy" />
                      </div>

                      <select
                        name="fieldOfStudy"
                        value={formik.values.fieldOfStudy}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm ${
                          formik.touched.fieldOfStudy && formik.errors.fieldOfStudy
                            ? "border-red-500"
                            : "focus:border-purple-500"
                        }`}
                      >
                        <option value="" disabled className="text-[#616262]/60">
                          {t("fieldOfStudyPlaceholder")}
                        </option>

                        {FIELDS.map((field) => (
                          <option key={field.id} value={field.id}>
                            {t(`fields.${field.id}`)}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}
                  {formik.values.fieldOfStudy == 12 && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("fieldOfStudyOtherPlaceholder")}
                        </label>
                      </div>

                      <input
                        name="fieldOfStudyOther"
                        value={formik.values.fieldOfStudyOther}
                        onChange={formik.handleChange}
                        type="text"
                        placeholder={t("specifyFieldOfStudy")}
                        className="w-full rounded-xl border-2 px-3 py-2"
                      />
                    </div>
                  )}

                  {formik.values.studentType == TYPES.OTHER && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("employmentStatusLabel")}
                        </label>
                        <ErrorMsg name="employmentStatus" />
                      </div>

                      <select
                        name="employmentStatus"
                        value={formik.values.employmentStatus}
                        onChange={formik.handleChange}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.employmentStatus && formik.errors.employmentStatus
                            ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      >
                        <option value="" disabled className="text-[#616262]/60">
                          {t("employmentStatusPlaceholder")}
                        </option>
                        {EMPLOYMENT_STATUS.map((s) => (
                          <option key={s.id} value={s.id}>
                            {t(`employmentStatus.${s.id}`)}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}
                  {formik.values.employmentStatus == 3 && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("employmentStatusOtherPlaceholder")}
                        </label>
                      </div>

                      <input
                        name="employmentStatusOther"
                        value={formik.values.employmentStatusOther}
                        onChange={formik.handleChange}
                        type="text"
                        placeholder={t("specifyEmploymentStatusPlaceholder")}
                        className="w-full rounded-xl border-2 px-3 py-2"
                      />
                    </div>
                  )}
                  {formik.values.studentType == TYPES.OTHER && formik.values.employmentStatus == 1 && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">{t("sectorLabel")}</label>
                        <ErrorMsg name="sector" />
                      </div>

                      <select
                        name="sector"
                        value={formik.values.sector}
                        onChange={formik.handleChange}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.sector && formik.errors.sector
                            ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      >
                        <option value="" disabled className="text-[#616262]/60">
                          {t("sectorPlaceholder")}
                        </option>
                        {SECTORS.map((s) => (
                          <option key={s.id} value={s.id}>
                            {t(`sectors.${s.id}`)}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}

                  {formik.values.studentType == TYPES.OTHER && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("educationLevelLabel")}
                        </label>
                        <ErrorMsg name="educationLevel" />
                      </div>

                      <select
                        name="educationLevel"
                        value={formik.values.educationLevel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.educationLevel && formik.errors.educationLevel
                            ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      >
                        <option value="" disabled className="text-[#616262]/60">
                          {t("educationLevelPlaceholder")}
                        </option>
                        {EDUCATION_LEVELS.map((level) => (
                          <option key={level.id} value={level.id}>
                            {t(`educationLevels.${level.id}`)}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}

                  {/* ministerial ID */}
                  {formik.values.studentType == TYPES.SCHOOLTEACHER && (
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("ministerialIdLabel")}
                        </label>
                        <ErrorMsg name={"ministerialID"} />
                      </div>
                      <input
                        type="text"
                        name={"ministerialID"}
                        placeholder={t("ministerialIdPlaceholder")}
                        value={formik.values.ministerialID}
                        min={0}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ""); // only digits
                          formik.setFieldValue("ministerialID", value);
                        }}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.ministerialID && formik.errors.ministerialID
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>
                  )}

                  {/* Nationality */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">
                        {t("nationalityLabel")}
                      </label>
                      <ErrorMsg name="nationality" />
                    </div>
                    <select
                      name="nationality"
                      value={formik.values.nationality}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.nationality && formik.errors.nationality
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("nationalityPlaceholder")}
                      </option>
                      <option value={NATIONALITIES.JORDAN}>{t("jordanian")}</option>
                      <option value={NATIONALITIES.SYRIA}>{t("syrian")}</option>
                      <option value={NATIONALITIES.OTHER}>{t("other")}</option>
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>

                  {/* National ID / Document ID */}
                  {formik.values.nationality && formik.values.nationality != NATIONALITIES.OTHER && (
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {formik.values.nationality == NATIONALITIES.SYRIA
                            ? t("documentIdLabel")
                            : t("nationalNumberLabel")}
                        </label>
                        <ErrorMsg
                          name={
                            formik.values.nationality == NATIONALITIES.SYRIA ? "documentId" : "nationalNumber"
                          }
                        />
                      </div>
                      <input
                        type="text"
                        name={
                          formik.values.nationality == NATIONALITIES.SYRIA ? "documentId" : "nationalNumber"
                        }
                        placeholder={
                          formik.values.nationality == NATIONALITIES.SYRIA
                            ? t("documentIdPlaceholder")
                            : t("nationalNumberPlaceholder")
                        }
                        value={
                          formik.values.nationality == NATIONALITIES.SYRIA
                            ? formik.values.documentId
                            : formik.values.nationalNumber
                        }
                        min={0}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ""); // only digits
                          if (formik.values.nationality == NATIONALITIES.SYRIA) {
                            formik.setFieldValue("documentId", value);
                          } else {
                            formik.setFieldValue("nationalNumber", value);
                          }
                        }}
                        maxLength={10}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.nationalNumber && formik.errors.nationalNumber
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />

                      {/* Syrian document attachment */}
                      {formik.values.nationality == NATIONALITIES.SYRIA && (
                        <div className="mt-2">
                          <div className="mb-1 flex items-center gap-2">
                            <label className="block text-sm font-bold text-[#616262]">
                              {t("documentFileLabel")}
                            </label>
                            <ErrorMsg name="documentFile" />
                          </div>
                          <label className="flex min-h-16 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-purple-300 bg-white/20 px-3 py-2 transition-all duration-300 hover:border-purple-500 hover:bg-purple-50">
                            <span className="break-all text-center text-sm text-[#616262]">
                              {formik.values.documentFile
                                ? formik.values.documentFile.name
                                : t("documentFilePlaceholder")}
                            </span>
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => {
                                formik.setFieldValue("documentFile", e.target.files[0] || null);
                                formik.setFieldTouched("documentFile", true, false);
                              }}
                              className="hidden"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-1 gap-x-4 gap-y-2">
                  {/* Email activation notice */}
                  <div className="rounded-xl border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-[#616262]">
                    <p>
                      <span className="text-red-500">*</span> {t("emailActivationNotice")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 md:grid-cols-2">
                    {/* Email */}
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">{t("emailLabel")}</label>
                        <ErrorMsg name="email" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        placeholder={t("emailPlaceholder")}
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.email && formik.errors.email
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>

                    {/* Confirm Email */}
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("confirmEmailLabel")}
                        </label>
                        <ErrorMsg name="confirmEmail" />
                      </div>
                      <input
                        type="email"
                        name="confirmEmail"
                        placeholder={t("confirmEmailPlaceholder")}
                        value={formik.values.confirmEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.confirmEmail && formik.errors.confirmEmail
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 md:grid-cols-2">
                    {/* Password */}
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">{t("password")}</label>
                        <ErrorMsg name="password" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder={t("passwordPlaceholder")}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.password && formik.errors.password
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-3 rtl:left-3"
                      >
                        {showPassword ? <FaEye /> : <FaEyeSlash />}
                      </button>
                    </div>

                    {/* Confirm Password */}
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("confirmPasswordLabel")}
                        </label>
                        <ErrorMsg name="confirmPassword" />
                      </div>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder={t("confirmPasswordPlaceholder")}
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.confirmPassword && formik.errors.confirmPassword
                            ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                            : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-3 rtl:left-3"
                      >
                        {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                      </button>
                    </div>
                  </div>

                  {/* heard about us */}
                  <div className="relative">
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">
                        {t("heardAboutUsLabel")}
                      </label>
                      <ErrorMsg name="heardAboutUs" />
                    </div>
                    <select
                      name="heardAboutUs"
                      value={formik.values.heardAboutUs}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                        formik.touched.heardAboutUs && formik.errors.heardAboutUs
                          ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "focus:border-purple-500 focus:ring-purple-300"
                      }`}
                    >
                      <option value="" disabled className="text-[#616262]/60">
                        {t("heardAboutUsPlaceholder")}
                      </option>
                      <option value={HEARD_ABOUT_US.LINKEDIN}>{t("LINKEDIN")}</option>
                      <option value={HEARD_ABOUT_US.FACEBOOK}>{t("FACEBOOK")}</option>
                      <option value={HEARD_ABOUT_US.SEARCH_ENGINE}>{t("SEARCH_ENGINE")}</option>
                      <option value={HEARD_ABOUT_US.JOCODES_HELLOGATE_PROGRAM_SOCIAL_MEDIA}>
                        {t("JOCODES_HELLOGATE_PROGRAM_SOCIAL_MEDIA")}
                      </option>
                      <option value={HEARD_ABOUT_US.FRIEND_INVITATION}>{t("FRIEND_INVITATION")}</option>
                      <option value={HEARD_ABOUT_US.CROWN_PRINCE_FOUNDATION_CPF}>
                        {t("CROWN_PRINCE_FOUNDATION_CPF")}
                      </option>
                      <option value={HEARD_ABOUT_US.MINISTRY_OF_DIGITAL_ECONOMY_AND_ENTREPRENEURSHIP_MODEE}>
                        {t("MINISTRY_OF_DIGITAL_ECONOMY_AND_ENTREPRENEURSHIP_MODEE")}
                      </option>
                      <option value={HEARD_ABOUT_US.FUTURE_STATIONS}>{t("FUTURE_STATIONS")}</option>
                      <option value={HEARD_ABOUT_US.OTHER}>{t("OTHER")}</option>
                    </select>
                    <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                  </div>
                  {/* Future station */}
                  {formik.values.heardAboutUs == HEARD_ABOUT_US.FUTURE_STATIONS && (
                    <div className="relative">
                      <div className="mb-1 flex items-center gap-2">
                        <label className="block text-sm font-bold text-[#616262]">
                          {t("futureStationLabel")}
                        </label>
                        <ErrorMsg name="futureStation" />
                      </div>

                      <select
                        name="futureStation"
                        value={formik.values.futureStation}
                        onChange={formik.handleChange}
                        className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                          formik.touched.futureStation && formik.errors.futureStation
                            ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "focus:border-purple-500 focus:ring-purple-300"
                        }`}
                      >
                        <option value="" disabled className="text-[#616262]/60">
                          {t("futureStationPlaceholder")}
                        </option>
                        {futureStationsList?.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.description}
                          </option>
                        ))}
                      </select>

                      <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
                    </div>
                  )}

                  {/* Referral Code (Optional) */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">
                        {t("referralCodeLabel")} ( {t("ifFound")} )
                      </label>
                    </div>
                    <input
                      type="text"
                      name="referralCode"
                      placeholder={t("referralCodePlaceholder")}
                      value={formik.values.referralCode}
                      onChange={formik.handleChange}
                      className={`w-full rounded-xl border-2 bg-white/20 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:border-purple-500 focus:outline-none focus:ring-purple-300 focus:ring-offset-1`}
                    />
                  </div>
                  {/* Promo Code (Optional) */}
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <label className="block text-sm font-bold text-[#616262]">
                        {t("promoCodeLabel")} ( {t("ifFound")} )
                      </label>
                    </div>
                    <input
                      type="text"
                      name="promoCode"
                      placeholder={t("promoCodePlaceholder")}
                      value={formik.values.promoCode}
                      onChange={formik.handleChange}
                      disabled={
                        formik.values.heardAboutUs == HEARD_ABOUT_US.FUTURE_STATIONS &&
                        !!formik.values.futureStation
                      }
                      className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:border-purple-500 focus:outline-none focus:ring-purple-300 focus:ring-offset-1 ${
                        formik.values.heardAboutUs == HEARD_ABOUT_US.FUTURE_STATIONS &&
                        !!formik.values.futureStation
                          ? "cursor-not-allowed bg-gray-100"
                          : "bg-white/20"
                      }`}
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-6 flex justify-between gap-6">
                {step == 1 ? (
                  <button
                    onClick={() => navigate("/auth/login")}
                    className="flex items-center gap-2 rounded-full py-2 text-sm text-purple-500"
                  >
                    {i18n.language == "ar" ? <FaChevronCircleRight /> : <FaChevronCircleLeft />}
                    <span>{t("backButton")}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="flex items-center gap-2 rounded-full py-2 text-sm text-purple-500"
                  >
                    {i18n.language == "ar" ? <FaChevronCircleRight /> : <FaChevronCircleLeft />}
                    <span>{t("backButton")}</span>
                  </button>
                )}

                {step < 2 ? (
                  <button
                    onClick={async () => {
                      const errors = await formik.validateForm();
                      const step1Fields = [
                        "firstName",
                        "secondName",
                        "thirdName",
                        "lastName",
                        "phone",
                        "ageRange",
                        "gender",
                        "city",
                        "studentType",
                        "nationality",
                        "nationalNumber",
                        "documentId",
                        "documentFile",
                        "ministerialID",
                        "schoolName",
                        "university",
                        "universityOther",
                        "fieldOfStudy",
                        "educationLevel",
                        "employmentStatus",
                        "sector",
                      ];
                      const step1Errors = step1Fields.filter((field) => errors[field]);

                      if (step1Errors.length === 0) {
                        setStep(step + 1);
                      } else {
                        formik.setTouched(
                          step1Fields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
                        );
                      }
                    }}
                    className="w-full rounded-full bg-purple-500 px-10 py-3 text-sm text-white"
                  >
                    {t("nextButton")}
                  </button>
                ) : (
                  <button
                    onClick={formik.handleSubmit}
                    className="w-full rounded-full bg-purple-500 py-3 text-sm text-white"
                  >
                    {loading ? (
                      <div className="flex flex-col items-center justify-center">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
                      </div>
                    ) : (
                      t("registerButton")
                    )}
                  </button>
                )}
              </div>
            </>
          </div>
        </div>

        {/* Footer Logos */}
        <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-white py-1">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/Modee2.png"
                alt="Modee"
                className="h-16 w-[75px] object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>

            <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/CPF2.png"
                alt="CPF"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
            <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/YTJ2.png"
                alt="YTJ"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
          </div>
          <div>
            <p className="text-center text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Designed and developed by HWK
            </p>
          </div>
        </footer>
        {hasReferralCode && (
          <div className="absolute left-0 top-0 flex h-full min-h-screen w-full items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
            <div className="max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
              <h1 className="mb-4 text-2xl font-semibold text-gray-800">{t("invite.welcome")}</h1>
              <p className="mb-6 text-[#616262]">{t("invite.description")}</p>
              <div className="mb-6 inline-flex select-all items-center justify-center rounded-lg bg-purple-100 px-6 py-3 font-mono text-lg font-semibold text-purple-800">
                {referralCode}
              </div>
              <button
                onClick={() => setHasReferralCode(false)}
                className="w-full rounded-lg bg-purple-600 py-3 font-semibold text-white transition hover:bg-purple-700"
              >
                {t("invite.getStarted")}
              </button>
              <p className="mt-4 text-sm text-gray-400">
                {t("invite.alreadyAccount")}{" "}
                <span className="cursor-pointer text-purple-600" onClick={() => navigate("/login")}>
                  {t("invite.loginHere")}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Registration;
